// Moderation queue — 05-admin §3. Reports list (GET /v1/admin/reports) + actions.
import { api, errMsg } from "../core/api.js";
import { h, mount } from "../core/dom.js";
import type { Page, Report, ReportState } from "../models.js";

const STATES: ReportState[] = ["open", "reviewing", "actioned", "dismissed"];
const STATE_LABEL: Record<ReportState, string> = {
  open: "열림",
  reviewing: "검토중",
  actioned: "조치됨",
  dismissed: "기각",
};

// Highest legal-risk reasons pinned to top (05 §3.1, R-001).
const PRIORITY = new Set(["csam_deepfake", "election_deepfake"]);
const REASON_LABEL: Record<string, string> = {
  csam_deepfake: "CSAM 딥페이크",
  election_deepfake: "선거 딥페이크",
  unlabeled_ai: "미표시 AI",
  copyright: "저작권",
  nsfw: "NSFW",
  harassment: "괴롭힘",
  hate: "혐오",
  violence: "폭력",
  impersonation: "사칭",
  privacy: "프라이버시",
  spam: "스팸",
  other: "기타",
};

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleString("ko-KR");
}

// Action dialog (05 §3.2): action requires a reason → audit_log.
function actionDialog(report: Report, onDone: () => void): void {
  const reasonField = h("input", { class: "field", placeholder: "사유 (필수, 감사 로그 기록)" }) as HTMLInputElement;
  const legalField = h("input", {
    class: "field",
    placeholder: "legal_basis (선택, 예: 성폭력처벌법 14조의2)",
  }) as HTMLInputElement;
  const status = h("p", { class: "muted small" }, "");

  const backdrop = h("div", { class: "dialog-backdrop" });
  function close(): void {
    backdrop.remove();
  }

  // POST /v1/admin/reports/{id}/action (01 §3.M) — backend contract is
  // { action: 'actioned'|'dismissed', note?, takedown_status? }. "테이크다운" resolves the
  // report as actioned AND takes the subject down (server cascades); "기각" dismisses it.
  // reason/legal_basis are folded into the audited `note`.
  async function runReportAction(kind: "takedown" | "dismiss"): Promise<void> {
    const reason = reasonField.value.trim();
    if (!reason) {
      status.textContent = "사유를 입력하세요.";
      status.className = "small error";
      return;
    }
    status.textContent = "처리 중…";
    status.className = "small muted";
    const legal = legalField.value.trim();
    const note = legal ? `${reason} (legal_basis: ${legal})` : reason;
    const body =
      kind === "takedown"
        ? { action: "actioned", takedown_status: "removed", note }
        : { action: "dismissed", note };
    try {
      await api.post(`/v1/admin/reports/${report.id}/action`, body);
      close();
      onDone();
    } catch (e) {
      status.textContent = `실패: ${errMsg(e)}`;
      status.className = "small error";
    }
  }

  async function moderateSubject(action: "removed" | "rejected"): Promise<void> {
    const reason = reasonField.value.trim();
    if (!reason) {
      status.textContent = "사유를 입력하세요.";
      status.className = "small error";
      return;
    }
    if (report.subject_type !== "variant") {
      status.textContent = "이 액션은 variant 대상에만 사용할 수 있습니다.";
      status.className = "small error";
      return;
    }
    status.textContent = "처리 중…";
    status.className = "small muted";
    try {
      // POST /v1/admin/variants/{id}/moderate (01 §3.M) — cascade(§3.2) is server-side.
      await api.post(`/v1/admin/variants/${report.subject_id}/moderate`, {
        action,
        reason,
        legal_basis: legalField.value.trim() || undefined,
      });
      close();
      onDone();
    } catch (e) {
      status.textContent = `실패: ${errMsg(e)}`;
      status.className = "small error";
    }
  }

  backdrop.appendChild(
    h(
      "div",
      { class: "dialog wide" },
      h("h3", {}, `신고 처리 — ${REASON_LABEL[report.reason] ?? report.reason}`),
      h(
        "p",
        { class: "muted small" },
        `대상: ${report.subject_type}/${report.subject_id}`,
      ),
      report.detail ? h("p", { class: "small" }, report.detail) : null,
      reasonField,
      legalField,
      h(
        "p",
        { class: "muted small" },
        "takedown/quarantine 시 서버가 cascade(파생 Cut/Short take-down + 매니페스트 거부, §3.2)를 적용합니다.",
      ),
      status,
      h(
        "div",
        { class: "row wrap" },
        h("button", { class: "btn danger", onclick: () => void runReportAction("takedown") }, "테이크다운"),
        h(
          "button",
          { class: "btn", onclick: () => void moderateSubject("rejected") },
          "variant 거부",
        ),
        h("button", { class: "btn secondary", onclick: () => void runReportAction("dismiss") }, "기각"),
        h("button", { class: "btn secondary", onclick: close }, "닫기"),
      ),
    ),
  );
  document.body.appendChild(backdrop);
}

function reportRow(r: Report, onDone: () => void): HTMLElement {
  return h(
    "tr",
    { class: PRIORITY.has(r.reason) ? "priority" : "" },
    h(
      "td",
      {},
      h("span", { class: `badge ${PRIORITY.has(r.reason) ? "danger" : ""}` }, REASON_LABEL[r.reason] ?? r.reason),
    ),
    h("td", {}, `${r.subject_type}/`, h("span", { class: "mono" }, r.subject_id)),
    h("td", { class: "muted small" }, r.detail ?? "—"),
    h("td", {}, h("span", { class: "badge" }, STATE_LABEL[r.state])),
    h("td", { class: "muted small" }, fmtTime(r.created_at)),
    h("td", {}, h("button", { class: "btn small", onclick: () => actionDialog(r, onDone) }, "처리")),
  );
}

export async function moderationView(_p: Record<string, string>, root: HTMLElement): Promise<void> {
  let activeState: ReportState = "open";

  const tabs = h("div", { class: "tabs" });
  const listWrap = h("div", { class: "card" });

  async function load(): Promise<void> {
    listWrap.replaceChildren(h("div", { class: "state muted" }, "신고 큐 불러오는 중…"));
    try {
      const page = await api.get<Page<Report>>(`/v1/admin/reports?state=${activeState}&limit=50`);
      if (page.items.length === 0) {
        listWrap.replaceChildren(h("div", { class: "state muted" }, `${STATE_LABEL[activeState]} 상태의 신고가 없습니다.`));
        return;
      }
      // Priority reasons pinned to top (§3.1).
      const sorted = [...page.items].sort((a, b) => {
        const pa = PRIORITY.has(a.reason) ? 0 : 1;
        const pb = PRIORITY.has(b.reason) ? 0 : 1;
        if (pa !== pb) return pa - pb;
        return b.created_at - a.created_at;
      });
      const table = h(
        "table",
        { class: "tbl" },
        h(
          "thead",
          {},
          h(
            "tr",
            {},
            h("th", {}, "사유"),
            h("th", {}, "대상"),
            h("th", {}, "상세"),
            h("th", {}, "상태"),
            h("th", {}, "접수"),
            h("th", {}, ""),
          ),
        ),
        h("tbody", {}, ...sorted.map((r) => reportRow(r, () => void load()))),
      );
      listWrap.replaceChildren(table);
    } catch (e) {
      listWrap.replaceChildren(h("div", { class: "state error" }, `신고 큐 불러오기 실패: ${errMsg(e)}`));
    }
  }

  function renderTabs(): void {
    tabs.replaceChildren(
      ...STATES.map((s) =>
        h(
          "button",
          {
            class: `tab ${s === activeState ? "active" : ""}`,
            onclick: () => {
              activeState = s;
              renderTabs();
              void load();
            },
          },
          STATE_LABEL[s],
        ),
      ),
    );
  }

  renderTabs();
  mount(
    root,
    h(
      "div",
      { class: "stack" },
      h("h1", {}, "모더레이션 큐"),
      h(
        "p",
        { class: "muted" },
        "신고 처리·테이크다운 (05-admin §3). CSAM/선거 딥페이크는 최상위 고정. 모든 액션은 사유 필수 → 감사 로그.",
      ),
      tabs,
      listWrap,
    ),
  );
  await load();
}
